import React, { useEffect, useState } from "react";
import { Pencil, X } from "lucide-react";
import { CardHeader } from "../../../components/cards";
import { Button } from "../../../components/button";
import { useUpdateCustomerNote } from "../hooks";

interface IProps {
  customerId: number;
  note?: string | null;
}

export const CustomerNoteCard: React.FC<IProps> = ({ customerId, note }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(note ?? "");
  const { updateNote, loading } = useUpdateCustomerNote();

  // La note peut arriver après le premier rendu (refetch, cache) : on resynchronise
  // tant que l'utilisateur n'est pas en train de la modifier.
  useEffect(() => {
    if (!isEditing) setValue(note ?? "");
  }, [note, isEditing]);

  const handleSave = async () => {
    const ok = await updateNote(customerId, value);
    if (ok) setIsEditing(false);
  };

  const handleCancel = () => {
    setValue(note ?? "");
    setIsEditing(false);
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <CardHeader
            title="Notes"
            subtitle="Contexte et informations sur le client"
          />
        </div>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center gap-1.5 py-1 px-2 text-xs text-indigo-500 dark:text-indigo-300 font-medium bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900 rounded flex-shrink-0"
          >
            <Pencil size={12} />
            Modifier
          </button>
        )}
      </div>

      {isEditing ? (
        <>
          <textarea
            className="input w-full mt-2"
            rows={6}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Contexte, historique d'échanges, informations glanées..."
            autoFocus
          />
          <div className="flex items-center gap-2 mt-3">
            <Button onClick={handleSave} loading={loading} disabled={loading}>
              Enregistrer
            </Button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              <X size={14} />
              Annuler
            </button>
          </div>
        </>
      ) : note ? (
        <p className="mt-2 text-sm text-gray-700 dark:text-zinc-300 whitespace-pre-line">
          {note}
        </p>
      ) : (
        <p className="mt-2 text-sm text-gray-400 dark:text-zinc-500 italic">
          Aucune note pour ce client
        </p>
      )}
    </div>
  );
};
